import {useState, useEffect, useMemo} from 'react';
import {AgentInfo} from '@/common-pages/proxy/proxy.type';
import {getAgentLevelTeamInfo} from '@/pages/agent/agent.service';
import {AgentLevelTeamInfoRes, AgentRuleLevelInfoRes} from '@/pages/agent/type';
import {AGENT_LEVEL_CONFIG} from '@/pages/agent/constants';
import globalStore from '@/services/global.state';

interface TeamCardData {
  level: number;
  levelConfig: any;
  cardData: {
    betRebate: string;
    rechargeRebate: string;
    teamSize: {
      current: number;
      target: number | string;
    };
    teamRecharge: {
      current: number;
      target: number | string;
    };
    reachedFlag: boolean;
  };
}

export function useUserDataSection(agentInfo?: AgentInfo) {
  const [teamInfo, setTeamInfo] = useState<AgentLevelTeamInfoRes | null>(null);
  const [loading, setLoading] = useState(false);

  // 获取用户当前等级，默认为1
  const userLevel = agentInfo?.agentLevel || 1;

  // 拉取团队信息 - 只有已登录时才请求
  useEffect(() => {
    const isLoggedIn = !!globalStore.token;

    // 未登录时不请求接口
    if (!isLoggedIn) {
      setTeamInfo(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getAgentLevelTeamInfo()
      .then(res => {
        if (!cancelled) {
          setTeamInfo(res);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatPercent = (v?: number) => `${((v ?? 0) * 100).toFixed(2)}%`;

  const findLevelItem = (
    list: AgentRuleLevelInfoRes[] | undefined,
    level: number,
  ) => list?.find(i => i.agentLevel === level);

  // 生成4个等级的卡片数据（优先使用接口数据，回退到 MOCK）
  const teamCards: TeamCardData[] = useMemo(() => {
    return [1, 2, 3, 4].map(level => {
      const levelConfig =
        AGENT_LEVEL_CONFIG[level as keyof typeof AGENT_LEVEL_CONFIG];

      const item = findLevelItem(teamInfo?.agentRuleLeveInfoResList, level);

      // 目标与当前值
      const currentUsers = item?.agentDirectUserCount ?? 0;
      const currentRecharge = item?.agentDirectUserRecharge ?? 0;
      const targetUsersRaw = item?.teamUserCount;
      const targetRechargeRaw = item?.teamRecharge;

      // 等级4无穷大规则：目标为 0 或缺失 -> 展示 ∞，不计算进度
      const showInfinityUsers = level === 4 && (targetUsersRaw ?? 0) === 0;
      const showInfinityRecharge =
        level === 4 && (targetRechargeRaw ?? 0) === 0;

      const targetUsers = showInfinityUsers
        ? ('∞' as const)
        : targetUsersRaw ?? 0;
      const targetRecharge = showInfinityRecharge
        ? ('∞' as const)
        : targetRechargeRaw ?? 0;

      const cardData = {
        betRebate: formatPercent(item?.betRate),
        rechargeRebate: formatPercent(item?.rechargeRate),
        teamSize: {
          current: currentUsers,
          target: targetUsers,
        },
        teamRecharge: {
          current: currentRecharge,
          target: targetRecharge,
        },
        reachedFlag: item?.reachedFlag === true,
      };

      return {level, levelConfig, cardData};
    });
  }, [teamInfo]);

  return {
    teamInfo,
    loading,
    userLevel,
    teamCards,
  };
}
